import { useState } from 'react'
import { Plus } from 'lucide-react'
import { Button, EmptyState, Input, Modal, Select, Toggle, toast } from '@/components/ui'
import { useAddSuppression, useSuppressions, useToggleSuppression } from '../api/queries'
import type { Suppression } from '../api/types'

const MODE_LABEL: Record<Suppression['mode'], string> = {
  exclude: 'Exclude from committed demand',
  near_term_only: 'Blanket order: count near-term picks only',
}

/**
 * Demand the review should not count as committed. Every filled field
 * must match a pick for the row to apply.
 */
export function SuppressionsPanel() {
  const { data, isLoading } = useSuppressions()
  const toggle = useToggleSuppression()
  const add = useAddSuppression()
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({
    order_name: '',
    customer_name: '',
    product_code: '',
    mode: 'exclude' as Suppression['mode'],
    reason: '',
  })

  const rows = data?.suppressions ?? []

  function submit() {
    add.mutate(
      {
        order_name: form.order_name.trim() || null,
        customer_name: form.customer_name.trim() || null,
        product_code: form.product_code.trim() || null,
        mode: form.mode,
        reason: form.reason.trim(),
      },
      {
        onSuccess: () => {
          toast.success('Suppression added. It applies from the next run.')
          setOpen(false)
          setForm({ order_name: '', customer_name: '', product_code: '', mode: 'exclude', reason: '' })
        },
        onError: (e) => toast.danger(`Not saved${e instanceof Error ? `: ${e.message}` : ''}`),
      },
    )
  }

  return (
    <section className="flex flex-col gap-4">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-primary">Suppressions</h2>
          <p className="text-sm text-secondary">
            Orders, customers or products the review should not treat as committed demand, such as
            blanket or release contracts. Every filled field must match.
          </p>
        </div>
        <Button variant="primary" onClick={() => setOpen(true)}>
          <Plus size={12} /> Add suppression
        </Button>
      </header>

      {isLoading ? (
        <div className="py-6 text-center text-sm text-tertiary">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState variant="no-data" title="No suppressions" description="All confirmed demand counts." />
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-secondary">
              <th className="py-1 pr-3">Order</th>
              <th className="py-1 pr-3">Customer</th>
              <th className="py-1 pr-3">Product code</th>
              <th className="py-1 pr-3">Mode</th>
              <th className="py-1 pr-3">Reason</th>
              <th className="py-1 pr-3">Added by</th>
              <th className="py-1 pr-3">Date</th>
              <th className="py-1">Active</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} className="border-b border-subtle">
                <td className="py-1 pr-3">{s.order_name ?? ''}</td>
                <td className="py-1 pr-3">{s.customer_name ?? ''}</td>
                <td className="py-1 pr-3">{s.product_code ?? ''}</td>
                <td className="py-1 pr-3">{MODE_LABEL[s.mode]}</td>
                <td className="py-1 pr-3">{s.reason}</td>
                <td className="py-1 pr-3">{s.added_by}</td>
                <td className="py-1 pr-3">{(s.created_at ?? '').slice(0, 10)}</td>
                <td className="py-1">
                  <Toggle
                    aria-label="Active"
                    checked={s.active}
                    onChange={(e) => toggle.mutate({ id: s.id, active: e.target.checked })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Modal
        open={open}
        onOpenChange={setOpen}
        title="Add suppression"
        description="Fill at least one of order, customer or product code."
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={add.isPending}
              disabled={
                form.reason.trim().length < 3 ||
                !(form.order_name.trim() || form.customer_name.trim() || form.product_code.trim())
              }
              onClick={submit}
            >
              Add
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Input
            aria-label="Order"
            placeholder="Order, e.g. S04084"
            value={form.order_name}
            onChange={(e) => setForm({ ...form, order_name: e.target.value })}
          />
          <Input
            aria-label="Customer"
            placeholder="Customer name contains"
            value={form.customer_name}
            onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
          />
          <Input
            aria-label="Product code"
            placeholder="Product internal reference"
            value={form.product_code}
            onChange={(e) => setForm({ ...form, product_code: e.target.value })}
          />
          <Select
            aria-label="Mode"
            value={form.mode}
            onChange={(e) => setForm({ ...form, mode: e.target.value as Suppression['mode'] })}
          >
            <option value="exclude">{MODE_LABEL.exclude}</option>
            <option value="near_term_only">{MODE_LABEL.near_term_only}</option>
          </Select>
          <Input
            aria-label="Reason"
            placeholder="Reason"
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
          />
        </div>
      </Modal>
    </section>
  )
}
